import { FolderOutlined, RightOutlined, LeftOutlined, FolderAddOutlined } from '@ant-design/icons';
import { Flex, List, Modal, Tag, Button } from 'antd';
import CustomBreadCrumbs from 'components/CustomBreadCrumbs';
import { useModal } from 'hooks/useModal';
import {
    Category
} from 'interfaces/documents';
import {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    fetchCategories
} from 'slices/categoriesActions';
import { closeModal } from 'slices/modalsSlice';
import { BreadcrumbItemType } from 'types';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';

export const modalName = 'categoriesTreeModal';

export interface ICategoriesTreeModalProps {
    title?: string;
    submitLabel?: string;
    cancelLabel?: string;
    excludedIds?: string[];
    parentId?: string;
};

export interface ICategoriesTreeModalCallbacks {
    onSubmit?: (value: Category | null) => void;
    onCancel?: () => void;
    onClose?: () => void;
}

const CategoriesTreeModal = ({ id, zIndex, props, callbacks }: { id: string, zIndex: number, props: ICategoriesTreeModalProps, callbacks: ICategoriesTreeModalCallbacks }) => {
    const dispatch = useAppDispatch();
    const {title, submitLabel, cancelLabel, excludedIds, parentId} = props;
    const { onSubmit, onCancel } = callbacks;
    const rootTitle = 'Documents';
    const [isOpen, setIsOpen] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState< Category | null >(null);
    const [hoveredId, setHoveredId] = useState<number | null>(null);
    const [initialLocation, setInitialLocation] = useState<{ id: number | null, name: string } | null>(null);
    const { list, excluded, breadcrumbs, loading, parent } = useAppSelector((state) => state.categories);

    useEffect(() => {
        if (props && !isOpen) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    }, [props]);

    const _fetchCategories = useCallback((categoryId: any) => {
        return fetchCategories(categoryId);
    }, [isOpen]);

    const close = () => {
        dispatch(closeModal({ modal: modalName, id }));
    };

    useEffect(() => {
        if (isOpen) {
            setSelectedCategory(null);
            dispatch(_fetchCategories(parentId || null)).unwrap().then((data: any) => {
                if (data.data.parent) {
                    setInitialLocation({ id: data.data.parent.id, name: data.data.parent.name });
                } else {
                    setInitialLocation({ id: null, name: rootTitle });
                }
            }).catch(() => {
                setInitialLocation({ id: null, name: rootTitle });
            });
        }
    }, [isOpen]);

    useEffect(() => {
        /**
         * Nothing to exclude
         */
        if (excluded.length == 0) {
            setIsDisabled(false);
            return;
        }

        /**
         * Root category (null) can't be excluded
         */
        if (!selectedCategory) {
            setIsDisabled(false);
            return;
        }

        /**
         * Find out if selected category excluded
         */
        const isExcluded = excluded.find((id: number) => {
            return id == selectedCategory?.id;
        });
        if (isExcluded) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [excluded, selectedCategory]);


    const handleSubmit = () => {
        onSubmit?.(selectedCategory || (parent || null));
        close();
    };

    const handleCancel = () => {
        onCancel?.();
        close();
    };
    const handleNavigate = (category: Category) => {
        dispatch(_fetchCategories(category.id));
    };

    const handleRowClick = (category: Category) => {
        if (excluded.includes(category.id)) return; // cannot select excluded
        if (selectedCategory?.id == category.id) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(category);
        }
    };

    const { openModal: openCategoryCreateModal } = useModal();

    const handleOpenCreatePrompt = () => {
        openCategoryCreateModal(
            'categoryCreateModal',
            {
                parent: parent ? parent.id : null,
            },
            {
                onSubmit: () => {
                    dispatch(_fetchCategories(parent ? parent.id : null));
                },
            },
        )
    }

    return <>
        <Modal
            zIndex={zIndex}
            title={title}
            open={isOpen}
            onCancel={handleCancel}
            onOk={handleSubmit}
            okText={submitLabel}
            cancelText={cancelLabel}
            okButtonProps={{ disabled: isDisabled || loading }}
            width={700}
        >
            {/* Current location row */}
            <Flex style={{ marginTop: 8, marginBottom: 8, gap: 8 }} align="center">
                {initialLocation && (
                    <>
                        <span style={{ color: '#6b7280' }}>Current location:</span>
                        <Tag
                            icon={<FolderOutlined />}
                            style={{ cursor: 'pointer' }}
                            onClick={() => dispatch(_fetchCategories(initialLocation.id))}
                        >
                            {initialLocation.name}
                        </Tag>
                    </>
                )}
            </Flex>

            {/* Folder header (current folder name with up-level arrow) */}
            {(() => {
                const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
                const currentCrumb = hasBreadcrumbs ? breadcrumbs[breadcrumbs.length - 1] : null;
                const upTargetId = hasBreadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].id : null;
                const canGoUp = hasBreadcrumbs; // if at root, there are no breadcrumbs
                const currentName = currentCrumb ? currentCrumb.name : (parent?.name || rootTitle);
                return (
                    <Flex style={{ marginTop: 12, marginBottom: 4, alignItems: 'center', gap: 8 }}>
                        <span
                            style={{ visibility: canGoUp ? 'visible' : 'hidden', cursor: canGoUp ? 'pointer' : 'default' }}
                            onClick={() => canGoUp && dispatch(_fetchCategories(upTargetId))}
                        >
                            <LeftOutlined />
                        </span>
                        <span style={{ fontWeight: 600 }}>{currentName}</span>
                    </Flex>
                );
            })()}
            {/* Scoped styles to hide scrollbar while keeping scroll functionality */}
            <style>{`
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                .no-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
            <Flex style={{ height: '320px' }}>
                <div className="no-scrollbar" style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
                <List
                    style={{ width: '100%' }}
                    loading={loading}
                    dataSource={list}
                    renderItem={(category: Category) => {
                        return (
                            <List.Item
                                key={category.id}
                                actions={category.has_children ? [
                                    <RightOutlined
                                        key="goto"
                                        style={{ visibility: hoveredId === category.id ? 'visible' : 'hidden' }}
                                        onClick={(e) => { e.stopPropagation(); handleNavigate(category); }}
                                    />,
                                ] : []}
                                style={{
                                    backgroundColor:
                                        selectedCategory?.id === category.id ? "#e6f7ff" : "transparent",
                                    cursor: 'pointer',
                                    width: '100%',
                                }}
                                onMouseEnter={() => setHoveredId(category.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                onClick={(e) => { e.stopPropagation(); handleRowClick(category); }}
                                onDoubleClick={(e) => { e.stopPropagation(); handleNavigate(category); }}
                            >
                                <List.Item.Meta
                                    avatar={<FolderOutlined />}
                                    title={category.name}
                                />
                            </List.Item>
                        );
                    }}
                />
                </div>
            </Flex>
            {/* Breadcrumbs moved below the folder navigation */}
            <Flex style={{ marginTop: 12 }}>
                <CustomBreadCrumbs
                    title={rootTitle}
                    variant={'h6'}
                    loading={loading}
                    breadcrumbs={breadcrumbs}
                    onRootClick={() => dispatch(_fetchCategories(null))}
                    onClick={(breadcrumb: any) => dispatch(_fetchCategories(breadcrumb?.id || null))}
                />
            </Flex>
            <Flex style={{ marginTop: 8 }}>
                <Button size="small" icon={<FolderAddOutlined />} onClick={handleOpenCreatePrompt}>
                    Create folder
                </Button>
            </Flex>
        </Modal>
    </>;
};

export default CategoriesTreeModal;
